<script setup lang="ts">
import { computed, ref, useId, watch } from "vue";
import { onClickOutside, onKeyStroke } from "@vueuse/core";

export interface VennSet {
  code: string;
  label: string;
  total: number;
  color: string;
}

export interface VennRegion {
  codes: string[];
  nb: number;
}

const props = defineProps({
  sets: { type: Array as () => VennSet[], required: true },
  regions: { type: Array as () => VennRegion[], required: true },
  respondents: { type: Number, required: true },
  none: { type: Number, default: 0 },
});

const WIDTH = 520;
const HEIGHT = 240;
const MARGIN = 10;

function inclusiveCount(codes: string[]): number {
  return props.regions
    .filter((region) => codes.every((code) => region.codes.includes(code)))
    .reduce((sum, region) => sum + region.nb, 0);
}

// Lens area of two circles of radii r1 / r2 whose centers are d apart.
function lensArea(r1: number, r2: number, d: number): number {
  if (d >= r1 + r2) return 0;
  if (d <= Math.abs(r1 - r2)) return Math.PI * Math.min(r1, r2) ** 2;
  const alpha = Math.acos((d * d + r1 * r1 - r2 * r2) / (2 * d * r1));
  const beta = Math.acos((d * d + r2 * r2 - r1 * r1) / (2 * d * r2));
  return r1 * r1 * (alpha - Math.sin(2 * alpha) / 2) + r2 * r2 * (beta - Math.sin(2 * beta) / 2);
}

// Distance between two centers so that their lens matches the target area (bisection).
function solveDistance(r1: number, r2: number, targetArea: number): number {
  let low = Math.abs(r1 - r2) + 0.001;
  let high = r1 + r2;
  if (targetArea <= 0) return high;
  for (let i = 0; i < 40; i++) {
    const mid = (low + high) / 2;
    if (lensArea(r1, r2, mid) > targetArea) low = mid;
    else high = mid;
  }
  return (low + high) / 2;
}

const layout = computed(() => {
  const sets = props.sets;
  if (sets.length < 2) return null;

  const maxTotal = Math.max(...sets.map((set) => set.total), 1);
  const areaPerUnit = (Math.PI * 95 * 95) / maxTotal;
  const radius = (total: number) => Math.max(14, Math.sqrt((total * areaPerUnit) / Math.PI));

  const r = sets.map((set) => radius(set.total));
  const d01 = solveDistance(r[0], r[1], inclusiveCount([sets[0].code, sets[1].code]) * areaPerUnit);

  let centers: { x: number; y: number }[];
  if (sets.length === 2) {
    centers = [
      { x: 0, y: 0 },
      { x: d01, y: 0 },
    ];
  } else {
    const d02 = solveDistance(r[0], r[2], inclusiveCount([sets[0].code, sets[2].code]) * areaPerUnit);
    const d12 = solveDistance(r[1], r[2], inclusiveCount([sets[1].code, sets[2].code]) * areaPerUnit);
    // Trilateration: the first center at the origin, the second on the x axis, the third below.
    const x2 = (d02 * d02 - d12 * d12 + d01 * d01) / (2 * d01);
    const y2 = Math.sqrt(Math.max(1, d02 * d02 - x2 * x2));
    centers = [
      { x: 0, y: 0 },
      { x: d01, y: 0 },
      { x: x2, y: y2 },
    ];
  }

  const minX = Math.min(...centers.map((c, i) => c.x - r[i]));
  const maxX = Math.max(...centers.map((c, i) => c.x + r[i]));
  const minY = Math.min(...centers.map((c, i) => c.y - r[i]));
  const maxY = Math.max(...centers.map((c, i) => c.y + r[i]));
  const scale = Math.min((WIDTH - 2 * MARGIN) / (maxX - minX), (HEIGHT - 2 * MARGIN) / (maxY - minY), 1);
  const offsetX = (WIDTH - (maxX - minX) * scale) / 2 - minX * scale;
  const offsetY = (HEIGHT - (maxY - minY) * scale) / 2 - minY * scale;

  return sets.map((set, i) => ({
    ...set,
    cx: centers[i].x * scale + offsetX,
    cy: centers[i].y * scale + offsetY,
    r: r[i] * scale,
  }));
});

function share(nb: number) {
  if (!props.respondents) return "0%";
  return `${Math.round((nb / props.respondents) * 100)}%`;
}

const wrapper = ref<HTMLElement | null>(null);
const svg = ref<SVGSVGElement | null>(null);
const hoveredCodes = ref<string[]>([]);
const selectedCodes = ref<string[]>([]);
const activeCodes = computed(() => (selectedCodes.value.length ? selectedCodes.value : hoveredCodes.value));
const cursor = ref({ x: 0, y: 0, flip: false });

function codesAt(event: MouseEvent): string[] {
  const ctm = svg.value?.getScreenCTM();
  if (!layout.value || !ctm) return [];
  const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(ctm.inverse());
  return layout.value
    .filter((circle) => (point.x - circle.cx) ** 2 + (point.y - circle.cy) ** 2 <= circle.r ** 2)
    .map((circle) => circle.code);
}

const sameCodes = (a: string[], b: string[]) => a.length === b.length && a.every((code) => b.includes(code));

function select(event: MouseEvent) {
  const codes = codesAt(event);
  if (!codes.length || sameCodes(codes, selectedCodes.value)) {
    selectedCodes.value = [];
    return;
  }
  const rect = wrapper.value?.getBoundingClientRect();
  if (rect) {
    const x = event.clientX - rect.left;
    cursor.value = { x, y: event.clientY - rect.top, flip: x > rect.width / 2 };
  }
  selectedCodes.value = codes;
}

watch(
  () => props.regions,
  () => (selectedCodes.value = []),
);
onClickOutside(wrapper, () => (selectedCodes.value = []));
onKeyStroke("Escape", () => (selectedCodes.value = []));

const panelStyle = computed(() => ({
  left: `${cursor.value.x}px`,
  top: `${cursor.value.y}px`,
  transform: cursor.value.flip ? "translate(calc(-100% - 14px), 14px)" : "translate(14px, 14px)",
}));

const hoverPanel = computed(() => {
  const codes = selectedCodes.value;
  if (!codes.length) return null;
  const nb = props.regions.find((region) => sameCodes(region.codes, codes))?.nb || 0;
  const others = props.sets.filter((set) => !codes.includes(set.code)).length;
  const picked = codes.length === 1 ? "Picked this answer" : `Picked ${codes.length === 2 ? "both" : "all three"} answers`;
  const excluded = others === 0 ? "" : `, not the ${others === 1 ? "other compared one" : "other two compared"}`;
  return {
    sets: props.sets.filter((set) => codes.includes(set.code)),
    caption: `${picked}${excluded}. Other answers of the question are not taken into account.`,
    nb,
    share: share(nb),
  };
});

// The lit zone: the active circles intersected (nested clip paths) minus the
// other circles (mask).
const uid = useId();
const highlight = computed(() => {
  if (!layout.value || !activeCodes.value.length) return null;
  return {
    inside: layout.value.filter((circle) => activeCodes.value.includes(circle.code)),
    outside: layout.value.filter((circle) => !activeCodes.value.includes(circle.code)),
  };
});
</script>

<template>
  <div
    v-if="layout"
    ref="wrapper"
    class="relative flex flex-col items-center gap-3">
    <svg
      ref="svg"
      :viewBox="`0 0 ${WIDTH} ${HEIGHT}`"
      class="h-auto w-full max-w-lg"
      :class="{ 'cursor-pointer': hoveredCodes.length }"
      @mousemove="hoveredCodes = codesAt($event)"
      @mouseleave="hoveredCodes = []"
      @click="select">
      <g style="mix-blend-mode: multiply">
        <circle
          v-for="circle in layout"
          :key="circle.code"
          :cx="circle.cx"
          :cy="circle.cy"
          :r="circle.r"
          :fill="circle.color"
          fill-opacity="0.55" />
      </g>
      <template v-if="highlight">
        <defs>
          <clipPath
            v-for="(circle, idx) in highlight.inside"
            :id="`${uid}-clip-${idx}`"
            :key="`clip-${circle.code}`">
            <circle
              :cx="circle.cx"
              :cy="circle.cy"
              :r="circle.r" />
          </clipPath>
          <mask :id="`${uid}-mask`">
            <rect
              :width="WIDTH"
              :height="HEIGHT"
              fill="white" />
            <circle
              v-for="circle in highlight.outside"
              :key="`mask-${circle.code}`"
              :cx="circle.cx"
              :cy="circle.cy"
              :r="circle.r"
              fill="black" />
          </mask>
        </defs>
        <g
          :mask="`url(#${uid}-mask)`"
          class="pointer-events-none">
          <g :clip-path="`url(#${uid}-clip-0)`">
            <g :clip-path="highlight.inside[1] ? `url(#${uid}-clip-1)` : undefined">
              <g :clip-path="highlight.inside[2] ? `url(#${uid}-clip-2)` : undefined">
                <rect
                  :width="WIDTH"
                  :height="HEIGHT"
                  class="fill-foreground"
                  fill-opacity="0.3" />
              </g>
            </g>
          </g>
        </g>
      </template>
    </svg>

    <ul class="flex max-w-xl flex-col gap-1 text-xs">
      <li
        v-for="set in sets"
        :key="set.code"
        class="flex items-start gap-2">
        <span
          class="mt-1 inline-block h-2 w-2 shrink-0 rounded-full"
          :style="{ backgroundColor: set.color }" />
        <span class="text-foreground">{{ set.label }}</span>
        <span class="ml-auto shrink-0 pl-3 tabular-nums text-muted-foreground">
          <span class="font-semibold text-foreground">{{ share(set.total) }}</span> ({{ set.total }})
        </span>
      </li>
      <li
        v-if="none > 0"
        class="flex items-start gap-2 text-muted-foreground">
        <span class="mt-1 inline-block h-2 w-2 shrink-0 rounded-full border border-dashed border-muted-foreground" />
        <span>None of these</span>
        <span class="ml-auto shrink-0 pl-3 tabular-nums">
          <span class="font-semibold">{{ share(none) }}</span> ({{ none }})
        </span>
      </li>
    </ul>

    <div
      v-if="hoverPanel"
      class="pointer-events-none absolute z-10 w-max max-w-[280px] rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground shadow-md"
      :style="panelStyle">
      <div class="flex flex-col gap-1">
        <span
          v-for="set in hoverPanel.sets"
          :key="set.code"
          class="flex items-start gap-1.5 font-semibold">
          <span
            class="mt-1 inline-block h-2 w-2 shrink-0 rounded-full"
            :style="{ backgroundColor: set.color }" />
          <span>{{ set.label }}</span>
        </span>
        <span class="opacity-90">{{ hoverPanel.caption }}</span>
        <span class="tabular-nums">
          <strong>{{ hoverPanel.nb }}</strong> respondents · <strong>{{ hoverPanel.share }}</strong> of the base
        </span>
      </div>
    </div>
  </div>
</template>
